import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Progress, Spinner,
} from "reactstrap";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── helpers ── */

const statusBadgeColor = (color) => color || "secondary";

const deltaClass  = (v) => (v > 0 ? "text-success" : v < 0 ? "text-danger" : "text-muted");
const deltaIcon   = (v) => (v > 0 ? "ri-arrow-up-line"   : v < 0 ? "ri-arrow-down-line" : "ri-subtract-line");
const deltaLabel  = (v) => (v > 0 ? `+${v}` : `${v}`);

const formatEur = (v) =>
    parseFloat(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

/* ── composant ── */

const Dashboard = () => {
    const [kpis,            setKpis]            = useState(null);
    const [servicesByType,  setServicesByType]  = useState([]);
    const [billingMonthly,  setBillingMonthly]  = useState([]);
    const [lastInvoices,    setLastInvoices]    = useState([]);
    const [contractSummary, setContractSummary] = useState(null);
    const [demandes,        setDemandes]        = useState([]);
    const [demandesCounts,  setDemandesCounts]  = useState({ total: 0, pending: 0, in_progress: 0, done: 0, error: 0 });
    const [tickets,         setTickets]         = useState([]);
    const [ticketsCounts,   setTicketsCounts]   = useState({ total: 0, open: 0, pending: 0, resolved: 0 });
    const [loading,         setLoading]         = useState(true);
    const [error,           setError]           = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const cfg = apiV2PostConfig();
            const [r1, r2, r3, r5, r6, r7, r8] = await Promise.allSettled([
                axios.post("/apiV2/", { action: "dashboard_kpis"              }, cfg),
                axios.post("/apiV2/", { action: "dashboard_services_by_type"  }, cfg),
                axios.post("/apiV2/", { action: "dashboard_billing_monthly", months: 6 }, cfg),
                axios.post("/apiV2/", { action: "dashboard_last_invoices",    limit: 7  }, cfg),
                axios.post("/apiV2/", { action: "dashboard_contract_summary"  }, cfg),
                axios.post("/apiV2/", { action: "mesdemandes", subaction: "list" }, cfg),
                axios.post("/apiV2/", { action: "tickets",     subaction: "list" }, cfg),
            ]);

            if (r1.status === "fulfilled") setKpis(r1.value?.data           ?? null);
            if (r2.status === "fulfilled") setServicesByType(r2.value?.data ?? []);
            if (r3.status === "fulfilled") setBillingMonthly(r3.value?.data ?? []);
            if (r5.status === "fulfilled") setLastInvoices(r5.value?.data   ?? []);
            if (r6.status === "fulfilled") setContractSummary(r6.value?.data ?? null);
            if (r7.status === "fulfilled") {
                const payload = r7.value?.data ?? r7.value ?? {};
                setDemandes(payload.items || []);
                setDemandesCounts(payload.counts || { total: 0, pending: 0, in_progress: 0, done: 0, error: 0 });
            }
            if (r8.status === "fulfilled") {
                const payload = r8.value?.data ?? r8.value ?? {};
                setTickets(payload.items || []);
                setTicketsCounts(payload.counts || { total: 0, open: 0, pending: 0, resolved: 0 });
            }

            const anyFailed = [r1,r2,r3,r5,r6,r7,r8].some((r) => r.status === "rejected");
            if (anyFailed) console.warn("Certains appels dashboard ont échoué.");
        } catch (err) {
            console.error("Dashboard fetch error:", err);
            setError("Impossible de charger le tableau de bord.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    /* ── max pour les barres de facturation (HT) ── */
    const maxBilling = billingMonthly.length
        ? Math.max(...billingMonthly.map((m) => parseFloat(m.amount_ht || 0)))
        : 1;

    /* ── répartition pour la synthèse contrat ── */
    const breakdown = contractSummary?.breakdown ?? [];

    /* ── état de chargement ── */
    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                        <div className="text-center">
                            <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                            <p className="text-muted mt-3 fw-medium">Chargement du tableau de bord…</p>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    /* ── état d'erreur total ── */
    if (error) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Card className="border-0 shadow-sm mt-4">
                        <CardBody className="text-center p-5">
                            <i className="ri-alert-line fs-1 text-danger mb-3 d-block"></i>
                            <h4 className="text-danger">Erreur de chargement</h4>
                            <p className="text-muted mb-4">{error}</p>
                            <button className="btn btn-primary" onClick={fetchAll}>
                                <i className="ri-refresh-line me-1"></i> Réessayer
                            </button>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Tableau de bord" pageTitle="Accueil" />

                    {/* ══════════════ ROW 1 ══════════════ */}
                    <Row>
                        {/* ── colonne gauche (5/12) ── */}
                        <Col xxl={5}>
                            <div className="d-flex flex-column h-100">

                                {/* Bandeau alerte factures */}
                                <Row className="h-100">
                                    <Col xs={12}>
                                        <Card>
                                            <CardBody className="p-0">
                                                {(kpis?.factures_impayees ?? 0) > 0 && (
                                                    <div className="alert alert-warning border-0 rounded-0 m-0 d-flex align-items-center" role="alert">
                                                        <i className="ri-alert-line text-warning me-2 icon-sm"></i>
                                                        <div className="flex-grow-1 text-truncate">
                                                            Vous avez <b>{kpis.factures_impayees}</b> facture{kpis.factures_impayees > 1 ? "s" : ""} impayée{kpis.factures_impayees > 1 ? "s" : ""}.
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <Link to="/mesfactures" className="text-reset text-decoration-underline">
                                                                <b>Voir</b>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                )}
                                                <Row className="align-items-end">
                                                    <Col sm={8}>
                                                        <div className="p-3">
                                                            <p className="fs-16 lh-base">
                                                                Bienvenue dans votre{" "}
                                                                <span className="fw-semibold">Espace Client</span>{" "}
                                                                IPTrend &rarr;
                                                            </p>
                                                            <div className="mt-3">
                                                                <Link to="/services" className="btn btn-success">
                                                                    Voir mes services
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col sm={4}>
                                                        <div className="px-3 text-center">
                                                            <i className="ri-phone-line"
                                                               style={{ fontSize: 90, color: "#0066A1", opacity: 0.15 }}
                                                            ></i>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* KPI ligne 1 */}
                                <Row>
                                    <Col md={6}>
                                        <Card className="card-animate">
                                            <CardBody>
                                                <div className="d-flex justify-content-between">
                                                    <div>
                                                        <p className="fw-medium text-muted mb-0">Services actifs</p>
                                                        <h2 className="mt-4 ff-secondary cfs-22 fw-semibold">
                                                            <CountUp end={kpis?.services_actifs ?? 0} duration={2} />
                                                        </h2>
                                                        <p className="mb-0 text-muted text-truncate">
                                                            <span className={`badge bg-light ${deltaClass(kpis?.services_actifs_delta)} mb-0`}>
                                                                <i className={`${deltaIcon(kpis?.services_actifs_delta)} align-middle`}></i>
                                                                {" "}{deltaLabel(kpis?.services_actifs_delta ?? 0)}
                                                            </span>{" "}
                                                            ce mois
                                                        </p>
                                                    </div>
                                                    <div className="avatar-sm flex-shrink-0">
                                                        <span className="avatar-title bg-success-subtle rounded-circle fs-2">
                                                            <i className="ri-checkbox-circle-line text-success"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>

                                    <Col md={6}>
                                        <Card className="card-animate">
                                            <CardBody>
                                                <div className="d-flex justify-content-between">
                                                    <div>
                                                        <p className="fw-medium text-muted mb-0">Lignes téléphoniques</p>
                                                        <h2 className="mt-4 ff-secondary cfs-22 fw-semibold">
                                                            <CountUp end={kpis?.lignes_telephoniques ?? 0} duration={2} />
                                                        </h2>
                                                        <p className="mb-0 text-muted text-truncate">
                                                            <span className="badge bg-light text-success mb-0">
                                                                <i className="ri-arrow-up-line align-middle"></i>{" "}
                                                                {kpis?.lignes_actives ?? 0} actives
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="avatar-sm flex-shrink-0">
                                                        <span className="avatar-title bg-info-subtle rounded-circle fs-2">
                                                            <i className="ri-phone-line text-info"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* KPI ligne 2 */}
                                <Row>
                                    <Col md={6}>
                                        <Card className="card-animate">
                                            <CardBody>
                                                <div className="d-flex justify-content-between">
                                                    <div>
                                                        <p className="fw-medium text-muted mb-0">Factures impayées</p>
                                                        <h2 className="mt-4 ff-secondary cfs-22 fw-semibold">
                                                            <CountUp end={kpis?.factures_impayees ?? 0} duration={2} />
                                                        </h2>
                                                        <p className="mb-0 text-muted text-truncate">
                                                            <span className={`badge bg-light ${deltaClass(kpis?.factures_impayees_delta)} mb-0`}>
                                                                <i className={`${deltaIcon(kpis?.factures_impayees_delta)} align-middle`}></i>
                                                                {" "}{deltaLabel(kpis?.factures_impayees_delta ?? 0)}
                                                            </span>{" "}
                                                            vs. mois précédent
                                                        </p>
                                                    </div>
                                                    <div className="avatar-sm flex-shrink-0">
                                                        <span className="avatar-title bg-warning-subtle rounded-circle fs-2">
                                                            <i className="ri-bill-line text-warning"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>

                                    <Col md={6}>
                                        <Card className="card-animate">
                                            <CardBody>
                                                <div className="d-flex justify-content-between">
                                                    <div>
                                                        <p className="fw-medium text-muted mb-0">Montant dû (TTC)</p>
                                                        <h2 className="mt-4 ff-secondary cfs-22 fw-semibold">
                                                            <CountUp
                                                                end={parseFloat(kpis?.montant_du_ttc ?? 0)}
                                                                duration={2}
                                                                decimals={2}
                                                                decimal=","
                                                                separator=" "
                                                                suffix=" €"
                                                            />
                                                        </h2>
                                                        <p className="mb-0 text-muted text-truncate">
                                                            <span className={`badge bg-light ${deltaClass(kpis?.montant_du_delta)} mb-0`}>
                                                                <i className={`${deltaIcon(kpis?.montant_du_delta)} align-middle`}></i>
                                                                {" "}{formatEur(kpis?.montant_du_delta ?? 0)}
                                                            </span>{" "}
                                                            vs. mois précédent
                                                        </p>
                                                    </div>
                                                    <div className="avatar-sm flex-shrink-0">
                                                        <span className="avatar-title bg-danger-subtle rounded-circle fs-2">
                                                            <i className="ri-money-euro-circle-line text-danger"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        {/* ── colonne droite (7/12) ── */}
                        <Col xxl={7}>
                            <Row className="h-100">
                                {/* Services par type */}
                                <Col xl={6}>
                                    <Card className="card-height-100">
                                        <CardHeader className="align-items-center d-flex">
                                            <h4 className="card-title mb-0 flex-grow-1">Services par type</h4>
                                            <Link to="/services" className="btn btn-soft-primary btn-sm material-shadow-none">
                                                Voir tout
                                            </Link>
                                        </CardHeader>
                                        <CardBody>
                                            {servicesByType.length === 0 ? (
                                                <p className="text-muted text-center py-4">Aucun service trouvé.</p>
                                            ) : (
                                                <div className="table-responsive table-card mt-3">
                                                    <table className="table table-borderless table-sm table-centered align-middle table-nowrap mb-1">
                                                        <thead className="text-muted border-dashed border border-start-0 border-end-0 bg-light-subtle">
                                                            <tr>
                                                                <th>Type de service</th>
                                                                <th style={{ width: "20%" }}>Qté</th>
                                                                <th style={{ width: "30%" }}>Statut</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="border-0">
                                                            {servicesByType.map((s, i) => (
                                                                <tr key={i}>
                                                                    <td>{s.type}</td>
                                                                    <td>{s.quantity}</td>
                                                                    <td>
                                                                        <Badge color={
                                                                            s.status === "Actif"    ? "success" :
                                                                            s.status === "Terminé"  ? "secondary" :
                                                                            s.status === "Brouillon"? "light" : "warning"
                                                                        }>
                                                                            {s.status}
                                                                        </Badge>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>

                                {/* Facturation mensuelle */}
                                <Col xl={6}>
                                    <Card className="card-height-100">
                                        <CardHeader className="align-items-center d-flex">
                                            <h4 className="card-title mb-0 flex-grow-1">Facturation mensuelle</h4>
                                            <Badge color="light" className="text-muted">HT</Badge>
                                        </CardHeader>
                                        <CardBody>
                                            {billingMonthly.length === 0 ? (
                                                <p className="text-muted text-center py-4">Aucune donnée de facturation.</p>
                                            ) : (
                                                billingMonthly.map((m, i) => {
                                                    const ht  = parseFloat(m.amount_ht  || 0);
                                                    const ttc = parseFloat(m.amount_ttc || 0);
                                                    return (
                                                        <div key={i} className="mb-3">
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span className="fs-13 text-muted">{m.month} {m.year}</span>
                                                                <span className="fs-13 fw-medium" title={`${formatEur(ttc)} TTC`}>
                                                                    {formatEur(ht)}
                                                                </span>
                                                            </div>
                                                            <Progress
                                                                value={maxBilling > 0 ? Math.round((ht / maxBilling) * 100) : 0}
                                                                color="info"
                                                                style={{ height: "6px" }}
                                                            />
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* ══════════════ ROW 2 ══════════════ */}
                    <Row>
                        {/* Synthèse contrat */}
                        <Col xl={6}>
                            <Card>
                                <CardHeader className="border-0 align-items-center d-flex">
                                    <h4 className="card-title mb-0 flex-grow-1">Synthèse contrat</h4>
                                </CardHeader>

                                <CardHeader className="p-0 border-0 bg-light-subtle">
                                    <Row className="g-0 text-center">
                                        <Col xs={6} sm={4}>
                                            <div className="p-3 border border-dashed border-start-0">
                                                <h5 className="mb-1">
                                                    <CountUp end={kpis?.services_actifs ?? 0} duration={2} />
                                                    <span className="text-success ms-1 fs-12">
                                                        {deltaLabel(kpis?.services_actifs_delta ?? 0)}
                                                        <i className="ri-arrow-right-up-line ms-1 align-middle"></i>
                                                    </span>
                                                </h5>
                                                <p className="text-muted mb-0">Services actifs</p>
                                            </div>
                                        </Col>
                                        <Col xs={6} sm={4}>
                                            <div className="p-3 border border-dashed border-start-0">
                                                <h5 className="mb-1">
                                                    <CountUp end={kpis?.lignes_actives ?? 0} duration={2} />
                                                    <span className="text-success ms-1 fs-12">
                                                        100%
                                                        <i className="ri-arrow-right-up-line ms-1 align-middle"></i>
                                                    </span>
                                                </h5>
                                                <p className="text-muted mb-0">Lignes actives</p>
                                            </div>
                                        </Col>
                                        <Col xs={6} sm={4}>
                                            <div className="p-3 border border-dashed border-start-0 border-end-0">
                                                <h5 className="mb-1">
                                                    <CountUp end={contractSummary?.availability_pct ?? 0} duration={2} suffix="%" />
                                                    <span className="text-success ms-1 fs-12">
                                                        <i className="ri-arrow-right-up-line ms-1 align-middle"></i>
                                                    </span>
                                                </h5>
                                                <p className="text-muted mb-0">Disponibilité</p>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardHeader>

                                <CardBody className="p-0 pb-2">
                                    <div className="p-3">
                                        {breakdown.length > 0 && (
                                            <Progress multi style={{ height: "8px", borderRadius: "4px" }} className="mb-3">
                                                {breakdown.map((item, i) => (
                                                    <Progress key={i} bar color={item.color} value={item.pct} />
                                                ))}
                                            </Progress>
                                        )}
                                        {breakdown.map((item, i) => (
                                            <div key={i} className="d-flex mb-2">
                                                <div className="flex-grow-1">
                                                    <p className="text-truncate text-muted fs-14 mb-0">
                                                        <i className={`mdi mdi-circle align-middle text-${item.color} me-2`}></i>
                                                        {item.label}
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <p className="mb-0">{item.pct}%</p>
                                                </div>
                                            </div>
                                        ))}
                                        {breakdown.length === 0 && (
                                            <p className="text-muted text-center py-2">Aucune répartition disponible.</p>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Mes demandes */}
                        <Col xl={3}>
                            <Card className="card-height-100" style={{ overflow: "hidden" }}>
                                <CardHeader className="align-items-center d-flex flex-shrink-0">
                                    <h4 className="card-title mb-0 flex-grow-1">
                                        <i className="ri-inbox-archive-line me-2 text-primary"></i>
                                        Mes demandes
                                    </h4>
                                    <Badge color="primary" pill className="me-2">{demandesCounts.total}</Badge>
                                    <Link to="/mes-demandes" className="btn btn-soft-primary btn-sm material-shadow-none">
                                        Voir tout
                                    </Link>
                                </CardHeader>
                                <CardBody className="p-0" style={{ overflowY: "auto" }}>
                                    {demandes.length === 0 ? (
                                        <p className="text-muted text-center py-4">Aucune demande.</p>
                                    ) : (
                                        <div className="vstack gap-0">
                                            {demandes.slice(0, 5).map((d, i) => {
                                                const col =
                                                    d.status === 0 ? "warning" :
                                                    d.status === 1 ? "info"    :
                                                    d.status === 2 ? "success" : "danger";
                                                const ico =
                                                    d.status === 0 ? "ri-time-line" :
                                                    d.status === 1 ? "ri-loader-4-line" :
                                                    d.status === 2 ? "ri-checkbox-circle-line" : "ri-close-circle-line";
                                                return (
                                                    <Link
                                                        key={i}
                                                        to="/mes-demandes"
                                                        className="d-flex align-items-start gap-2 px-3 py-2 border-bottom text-decoration-none"
                                                    >
                                                        <div className="avatar-xs flex-shrink-0">
                                                            <span className={`avatar-title rounded-circle bg-${col}-subtle text-${col} fs-14`}>
                                                                <i className={ico}></i>
                                                            </span>
                                                        </div>
                                                        <div className="flex-grow-1 min-width-0">
                                                            <p className="mb-0 fs-13 fw-medium text-truncate text-body">
                                                                {d.offer_label || d.offer}
                                                            </p>
                                                            <p className="mb-0 text-muted fs-11 text-truncate">
                                                                {d.phone_line_formatted || d.phone_line || `#${d.idsale}`}
                                                            </p>
                                                        </div>
                                                        <Badge className={`bg-${col}-subtle text-${col} fs-10 align-self-center`}>
                                                            {d.status_label}
                                                        </Badge>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Tickets */}
                        <Col xl={3}>
                            <Card className="card-height-100" style={{ overflow: "hidden" }}>
                                <CardHeader className="align-items-center d-flex flex-shrink-0">
                                    <h4 className="card-title mb-0 flex-grow-1">
                                        <i className="ri-customer-service-2-line me-2 text-primary"></i>
                                        Tickets
                                    </h4>
                                    <Badge color="primary" pill className="me-2">{ticketsCounts.total}</Badge>
                                    <Link to="/support" className="btn btn-soft-primary btn-sm material-shadow-none">
                                        Voir tout
                                    </Link>
                                </CardHeader>
                                <CardBody className="p-0" style={{ overflowY: "auto" }}>
                                    {tickets.length === 0 ? (
                                        <p className="text-muted text-center py-4">Aucun ticket.</p>
                                    ) : (
                                        <div className="vstack gap-0">
                                            {tickets.slice(0, 5).map((t, i) => {
                                                const st = t.status;
                                                const col =
                                                    (st === 0 || st === 1)            ? "primary"   :
                                                    (st === 2 || st === 3)            ? "info"      :
                                                    (st === 5 || st === 7)            ? "warning"   :
                                                    (st === 8)                        ? "success"   :
                                                    (st === 9)                        ? "secondary" : "secondary";
                                                return (
                                                    <Link
                                                        key={i}
                                                        to="/support"
                                                        className="d-flex align-items-start gap-2 px-3 py-2 border-bottom text-decoration-none"
                                                    >
                                                        <div className="avatar-xs flex-shrink-0">
                                                            <span className={`avatar-title rounded-circle bg-${col}-subtle text-${col} fs-14`}>
                                                                <i className="ri-ticket-2-line"></i>
                                                            </span>
                                                        </div>
                                                        <div className="flex-grow-1 min-width-0">
                                                            <p className="mb-0 fs-13 fw-medium text-truncate text-body">
                                                                {t.subject}
                                                            </p>
                                                            <p className="mb-0 text-muted fs-11 text-truncate">
                                                                {t.ref}
                                                            </p>
                                                        </div>
                                                        <Badge className={`bg-${col}-subtle text-${col} fs-10 align-self-center`}>
                                                            {t.status_label}
                                                        </Badge>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* ══════════════ ROW 3 ══════════════ */}
                    <Row>
                        {/* Répartition des services */}
                        <Col xl={4}>
                            <Card className="card-height-100">
                                <CardHeader className="align-items-center d-flex">
                                    <h4 className="card-title mb-0 flex-grow-1">Répartition des services</h4>
                                </CardHeader>
                                <CardBody>
                                    {servicesByType.length === 0 ? (
                                        <p className="text-muted text-center py-4">Aucune donnée.</p>
                                    ) : (
                                        <div className="table-responsive mt-3">
                                            <table className="table table-borderless table-sm table-centered align-middle table-nowrap mb-0">
                                                <tbody className="border-0">
                                                    {servicesByType.map((s, i) => {
                                                        const colors = ["text-primary","text-warning","text-info","text-danger","text-success"];
                                                        const col = colors[i % colors.length];
                                                        return (
                                                            <tr key={i}>
                                                                <td>
                                                                    <h4 className="text-truncate fs-14 fw-medium mb-0">
                                                                        <i className={`ri-stop-fill align-middle fs-18 ${col} me-2`}></i>
                                                                        {s.type}
                                                                    </h4>
                                                                </td>
                                                                <td>
                                                                    <p className="text-muted mb-0">{s.quantity} ligne{s.quantity > 1 ? "s" : ""}</p>
                                                                </td>
                                                                <td className="text-end">
                                                                    <Badge color={s.status === "Actif" ? "success" : "warning"}>
                                                                        {s.status}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Résumé contrat */}
                        <Col xl={4} md={6}>
                            <Card className="card-height-100">
                                <CardHeader className="align-items-center d-flex">
                                    <h4 className="card-title mb-0 flex-grow-1">Résumé contrat</h4>
                                    <Link to="/services" className="btn btn-soft-primary btn-sm material-shadow-none">
                                        Voir détails
                                    </Link>
                                </CardHeader>
                                <CardBody>
                                    {!contractSummary ? (
                                        <p className="text-muted text-center py-4">Aucun contrat trouvé.</p>
                                    ) : (
                                        <>
                                            <Row className="align-items-center">
                                                <Col xs={6}>
                                                    <h6 className="text-muted text-uppercase fw-semibold text-truncate fs-12 mb-3">
                                                        Contrat principal
                                                    </h6>
                                                    <h4 className="mb-0">{contractSummary.ref}</h4>
                                                    <p className="mb-0 mt-2 text-muted">
                                                        <span className="badge bg-success-subtle text-success mb-0">
                                                            <i className="ri-arrow-up-line align-middle"></i>{" "}
                                                            {contractSummary.status}
                                                        </span>
                                                    </p>
                                                </Col>
                                                <Col xs={6}>
                                                    <div className="text-center">
                                                        <i className="ri-file-text-line"
                                                           style={{ fontSize: 80, color: "#0066A1", opacity: 0.15 }}
                                                        ></i>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div className="mt-3 pt-2">
                                                {breakdown.map((item, i) => (
                                                    <div key={i} className="d-flex mb-2">
                                                        <div className="flex-grow-1">
                                                            <p className="text-truncate text-muted fs-14 mb-0">
                                                                <i className={`mdi mdi-circle align-middle text-${item.color} me-2`}></i>
                                                                {item.label}
                                                            </p>
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <p className="mb-0">{item.pct}%</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="mt-2 text-center">
                                                <Link to="/services" className="text-muted text-decoration-underline">
                                                    Voir tous les services
                                                </Link>
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Dernières factures */}
                        <Col xl={4} md={6}>
                            <Card className="card-height-100" style={{ overflow: "hidden" }}>
                                <CardHeader className="align-items-center d-flex flex-shrink-0">
                                    <h4 className="card-title mb-0 flex-grow-1">Dernières factures</h4>
                                    <Badge color="light" className="text-muted me-2">TTC</Badge>
                                    <Link to="/mesfactures" className="btn btn-soft-primary btn-sm material-shadow-none">
                                        Voir tout
                                    </Link>
                                </CardHeader>
                                <CardBody style={{ overflowY: "auto" }}>
                                    {lastInvoices.length === 0 ? (
                                        <p className="text-muted text-center py-4">Aucune facture trouvée.</p>
                                    ) : (
                                        <div className="table-responsive table-card">
                                            <table className="table align-middle table-borderless table-centered table-nowrap mb-0">
                                                <thead className="text-muted table-light">
                                                    <tr>
                                                        <th>Référence</th>
                                                        <th>Montant à payer</th>
                                                        <th>Mode paiement</th>
                                                        <th>Statut</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lastInvoices.map((inv, i) => {
                                                        const toPay = parseFloat(inv.amount_to_pay ?? inv.amount_ttc ?? 0);
                                                        const ttc   = parseFloat(inv.amount_ttc ?? 0);
                                                        return (
                                                        <tr key={i}>
                                                            <td>
                                                                <Link to="/mesfactures" className="text-body">
                                                                    {inv.ref}
                                                                </Link>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={toPay > 0 ? "fw-semibold text-danger" : "text-muted"}
                                                                    title={`Total facture : ${formatEur(ttc)} TTC`}
                                                                >
                                                                    {formatEur(toPay)}
                                                                </span>
                                                            </td>
                                                            <td className="text-muted fs-12">
                                                                {inv.payment_mode_label || "—"}
                                                            </td>
                                                            <td>
                                                                <Badge color={statusBadgeColor(inv.status_color)}>
                                                                    {inv.status}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Dashboard;
